/**
 * "Track these personas": the wire contract of importing a free prompt
 * generation (its prompts and persona drafts) into a property, and of
 * the claim route that picks the property.
 *
 * Client-safe: types, a zod request schema and the error codes, no runtime
 * dependencies beyond zod.
 */

import { z } from "zod";
import type { PersonaDTO } from "./personas";

/** Most prompts one import takes (the free tool writes at most ~60). */
export const GENERATION_IMPORT_MAX_PROMPTS = 200;
/** Most persona drafts one import takes (the free tool drafts 3 to 5). */
export const GENERATION_IMPORT_MAX_PERSONAS = 20;
/** Longest project name the import accepts (the projects API's cap). */
export const GENERATION_IMPORT_PROJECT_NAME_MAX_CHARS = 200;

/**
 * `POST /personas/by-property/:propertyId/import-generation/preview`: what
 * importing this selection would do. Nothing is written.
 */
export const GenerationImportPreviewRequestSchema = z.object({
	generationId: z.string().trim().min(1).max(128),
	/** Ids of the generation's result items (its `results[].id`). */
	promptIds: z
		.array(z.string().min(1).max(128))
		.max(GENERATION_IMPORT_MAX_PROMPTS),
	/** Indexes into the generation's parsed `personaDrafts`. */
	personaDraftIndexes: z
		.array(z.number().int().min(0).max(99))
		.max(GENERATION_IMPORT_MAX_PERSONAS),
	/** Also create a project that asks as the imported personas. */
	createProject: z.boolean().optional(),
});
export type GenerationImportPreviewRequest = z.infer<
	typeof GenerationImportPreviewRequestSchema
>;

/** `POST /personas/by-property/:propertyId/import-generation`. */
export const GenerationImportRequestSchema =
	GenerationImportPreviewRequestSchema.extend({
		/** The new project's name; defaults to "<property> personas". */
		projectName: z
			.string()
			.trim()
			.max(GENERATION_IMPORT_PROJECT_NAME_MAX_CHARS)
			.optional(),
	});
export type GenerationImportRequest = z.infer<
	typeof GenerationImportRequestSchema
>;

/**
 * Why an import (or its preview) was refused. The personas router maps each
 * to a status: NOT_FOUND 404, NOT_COMPLETE 409, ALREADY_CLAIMED 409 (another
 * organization imported it first; only its members may import it again),
 * EXPIRED 410, the selection codes 400, PROPERTY_NOT_FOUND 404,
 * PROPERTY_UNSUPPORTED 409, INSUFFICIENT_CREDITS 402.
 */
export const GENERATION_IMPORT_ERROR_CODES = [
	"GENERATION_NOT_FOUND",
	"GENERATION_NOT_COMPLETE",
	"GENERATION_ALREADY_CLAIMED",
	"GENERATION_EXPIRED",
	"IMPORT_EMPTY_SELECTION",
	"IMPORT_INVALID_SELECTION",
	"IMPORT_PROPERTY_NOT_FOUND",
	"IMPORT_PROPERTY_UNSUPPORTED",
	"IMPORT_INSUFFICIENT_CREDITS",
] as const;
export type GenerationImportErrorCode =
	(typeof GENERATION_IMPORT_ERROR_CODES)[number];

/**
 * The organization's daily runs before and after the import, and what the
 * added runs cost. One unit is one prompt (or one location or persona
 * variant of it) run once a day. Prompts only run inside an ACTIVE project,
 * so an import without a project adds no units.
 */
export interface GenerationImportAllowance {
	/** Units the plan includes (0 without an allocation). */
	includedUnits: number;
	unitsInUseBefore: number;
	unitsInUseAfter: number;
	netUnits: number;
	includedOfAdded: number;
	billedOfAdded: number;
	billedUnitsAfter: number;
	additionalCreditsPer30Days: number;
	isOverAllowance: boolean;
	/** The plan includes a unit allocation (and the subscription is live). */
	hasAllocation: boolean;
	/** False when the billed units need more credits than the balance holds. */
	canProceed: boolean;
	/** Credits charged at import: the first 30-run cycle of each billed unit. */
	creditsRequired: number;
	creditBalance: number;
	/** Credits one billed unit costs per 30 days. */
	creditsPerUnitPer30Days: number;
}

/** The property the import lands in (a LOCATION resolves to its parent). */
export interface GenerationImportTarget {
	propertyId: string;
	name: string;
	domain: string | null;
	subjectType: string;
	/** Active prompts in the property before the import. */
	promptCount: number;
	/** Non-archived personas before the import. */
	personaCount: number;
	activeProjectCount: number;
	/** The property tracks the domain the generation was made for. */
	matchesGenerationDomain: boolean;
	/**
	 * The property the generation's prompts were written to: it moved into
	 * this organization when its domain was added, so the prompts are here
	 * already. The import keeps the selected ones and switches off the ones
	 * the generation created that are left out (unless a project uses them).
	 */
	isGenerationProperty: boolean;
}

export interface GenerationImportPreview {
	generationId: string;
	/** The domain the generation was made for. */
	domain: string | null;
	target: GenerationImportTarget;
	/** Selected prompts. */
	promptCount: number;
	/** Of those, how many the import adds to the property. */
	newPromptCount: number;
	/**
	 * Of those, how many the property already has (skipped): word for word,
	 * or the generation's own prompts once an earlier import claimed it.
	 */
	duplicatePromptCount: number;
	/** Discovery prompts the project would run per persona. */
	discoveryPromptCount: number;
	/** Brand prompts in the selection (they run without personas). */
	brandPromptCount: number;
	/** Selected persona drafts. */
	personaCount: number;
	/** Of those, how many this property already has from an earlier import. */
	existingPersonaCount: number;
	/**
	 * Whether a project would be created: asked for, with at least one
	 * persona and one discovery prompt selected.
	 */
	createProject: boolean;
	/** Daily runs the project's prompts add (0 without a project). */
	promptUnits: number;
	/** Daily runs the project's personas add (0 without a project). */
	personaUnits: number;
	allowance: GenerationImportAllowance;
}

export interface GenerationImportResult {
	propertyId: string;
	/** Prompts this import added to the property. */
	importedPrompts: number;
	/**
	 * Selected prompts the property already had: word for word, or the
	 * generation's own prompts once an earlier import claimed it.
	 */
	skippedDuplicatePrompts: number;
	/** The imported personas (new, or reused from an earlier import). */
	personas: PersonaDTO[];
	/** Of `personas`, how many the property already had (nothing created). */
	existingPersonaCount: number;
	project: { id: string; name: string | null; personaCount: number } | null;
	/**
	 * Set when a project was asked for but not created. "insufficient_credits":
	 * the organization ran out of credits between the preview and the import.
	 */
	projectSkippedReason: "insufficient_credits" | null;
}

/** One property a claim can land in. */
export interface GenerationClaimPropertyOption {
	id: string;
	name: string;
	domain: string | null;
	subjectType: string;
	organizationId: string;
	organizationSlug: string;
	organizationName: string;
	promptCount: number;
	personaCount: number;
}

/** What the claim route does with a generation for the signed-in user. */
export type GenerationClaimResolution =
	| { status: "not_found"; generationId: string }
	| { status: "expired"; generationId: string; domain: string | null }
	| {
			status: "ready";
			generationId: string;
			domain: string;
			/** Properties in the user's organizations that track the domain. */
			matches: GenerationClaimPropertyOption[];
			/** Other properties the user could import into. */
			others: GenerationClaimPropertyOption[];
	  };
